import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import HelpGuide from './HelpGuide';

const API_BASE = 'https://api.camel.optitrac.com';

interface Vehicle {
  unitId: string; name: string; licensePlate: string; platform: 'Camel' | 'Starlink';
  vehicleId?: string; fleet?: string;
}

interface CheckItem {
  id: string; label: string; icon: string; passed: boolean; timestamp: string | null;
  description: string; extra?: string; detect: (events: any[]) => { passed: boolean; time: string | null; extra?: string };
}

const getHeaders = () => {
  const token = localStorage.getItem('clientToken') || localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const InstallCheck: React.FC = () => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedFleets, setSelectedFleets] = useState<string[]>([]);
  const [selectedPlatform, setSelectedPlatform] = useState<string>('All');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [checkRunning, setCheckRunning] = useState(false);
  const [checkItems, setCheckItems] = useState<CheckItem[]>([]);
  const [disabledChecks, setDisabledChecks] = useState<Set<string>>(new Set());
  const optionalChecks = new Set(['dump_up', 'dump_down', 'eng_on', 'eng_off', 'immo_armed', 'immo_disarmed']);
  const [refreshRate, setRefreshRate] = useState(20);
  const [countdown, setCountdown] = useState(0);
  const [checkStartTime, setCheckStartTime] = useState<string | null>(null);
  const [liveBaseline, setLiveBaseline] = useState<string | null>(null);
  const [showLivePopup, setShowLivePopup] = useState(false);
  const [history, setHistory] = useState<any[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showWiring, setShowWiring] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [deviceLocation, setDeviceLocation] = useState<{lat: number, lng: number} | null>(null);
  const [showCssInfo, setShowCssInfo] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [viewMode, setViewMode] = useState<'select' | 'saved'>('select');
  const [allSavedChecks, setAllSavedChecks] = useState<any[]>([]);
  const [savedChecksSearch, setSavedChecksSearch] = useState('');
  const [loadingSavedChecks, setLoadingSavedChecks] = useState(false);
  const intervalRef = useRef<any>(null);
  const countdownRef = useRef<any>(null);

  const clientUser = localStorage.getItem('clientUser') || localStorage.getItem('user');
  const currentUser = clientUser ? JSON.parse(clientUser) : null;
  const isClient = currentUser?.type === 'client' || currentUser?.type === 'client_user';

  // Fetch fleet
  useEffect(() => {
    const fetchFleet = async () => {
      try {
        const headers = getHeaders();
        const fleet: Vehicle[] = [];

        if (isClient) {
          const devRes = await axios.get(`${API_BASE}/api/devices/my-devices`, { headers });
          const myDevices = devRes.data || [];
          const myHex = new Set(myDevices.map((d: any) => d.hex_value?.toUpperCase()).filter(Boolean));
          const [camelRes, starlinkRes] = await Promise.allSettled([
            axios.get(`${API_BASE}/api/camel/vehicles`, { headers }),
            axios.get(`${API_BASE}/api/starlink/vehicles?page=1&limit=500`, { headers })
          ]);
          if (camelRes.status === 'fulfilled') {
            (camelRes.value.data || []).forEach((v: any) => {
              if (myHex.has(v.unitId?.toUpperCase()))
                fleet.push({ unitId: v.unitId, name: v.name || v.licensePlate, licensePlate: v.licensePlate, platform: 'Camel', fleet: v.fleet });
            });
          }
          if (starlinkRes.status === 'fulfilled') {
            const st = starlinkRes.value.data?.vehicles || starlinkRes.value.data || [];
            st.forEach((v: any) => {
              if (myHex.has(v.unitId?.toUpperCase()))
                fleet.push({ unitId: v.unitId, name: v.name || v.licensePlate || v.unitId, licensePlate: v.licensePlate || v.name || '', platform: 'Starlink', fleet: v.fleet });
            });
          }
        } else {
          // Admin: fetch all
          const [camelRes, starlinkRes] = await Promise.allSettled([
            axios.get(`${API_BASE}/api/camel/vehicles`, { headers }),
            axios.get(`${API_BASE}/api/starlink/vehicles?page=1&limit=500`, { headers })
          ]);
          if (camelRes.status === 'fulfilled') {
            (camelRes.value.data || []).forEach((v: any) => {
              fleet.push({ unitId: v.unitId, name: v.name || v.licensePlate, licensePlate: v.licensePlate, platform: 'Camel', fleet: v.fleet });
            });
          }
          if (starlinkRes.status === 'fulfilled') {
            const st = starlinkRes.value.data?.vehicles || starlinkRes.value.data || [];
            st.forEach((v: any) => {
              fleet.push({ unitId: v.unitId, name: v.name || v.licensePlate || v.unitId, licensePlate: v.licensePlate || v.name || '', platform: 'Starlink', fleet: v.fleet });
            });
          }
        }
        fleet.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        // Deduplicate by unitId+platform (allow one per platform, no dupes within same platform)
        const seen = new Set<string>();
        const unique = fleet.filter(v => {
          const key = `${v.platform}:${v.unitId?.toUpperCase()}`;
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setVehicles(unique);
      } catch (e) { console.error('Error fetching fleet:', e); }
      finally { setLoading(false); }
    };
    fetchFleet();
  }, [isClient]);

  const buildCheckItems = useCallback((): CheckItem[] => [
    { id: 'vin', label: 'Vehicle Battery (VIN)', icon: '🔋', passed: false, timestamp: null, description: 'Battery voltage reading received',
      detect: (evts) => { const e = evts.find((ev: any) => ev.vin && parseFloat(ev.vin) > 0); if (!e) return { passed: false, time: null }; const v = parseFloat(e.vin); return { passed: true, time: e.time || null, extra: `${v.toFixed(1)}V${v < 12 ? ' ⚠️ LOW VOLTAGE' : ''}` }; }},
    { id: 'vbat', label: 'Device Battery (VBAT)', icon: '🪫', passed: false, timestamp: null, description: 'Device backup battery reading',
      detect: (evts) => { const e = evts.find((ev: any) => ev.vbat && parseFloat(ev.vbat) > 0); if (!e) return { passed: false, time: null }; const v = parseFloat(e.vbat); return { passed: true, time: e.time || null, extra: `${v.toFixed(2)}V${v < 3.71 ? ' ⚠️ LOW VOLTAGE' : ''}` }; }},
    { id: 'alt', label: 'Altitude / GPS', icon: '📡', passed: false, timestamp: null, description: 'GPS altitude fix received',
      detect: (evts) => { const e = evts.find((ev: any) => ev.alt !== undefined && ev.alt !== null && ev.alt !== 0); return { passed: !!e, time: e?.time || null }; }},
    { id: 'sat', label: 'Satellites', icon: '🛰️', passed: false, timestamp: null, description: 'Satellite connection established',
      detect: (evts) => { const e = evts.find((ev: any) => ev.sat && parseInt(ev.sat) > 0); return { passed: !!e, time: e?.time || null }; }},
    { id: 'css', label: 'Cellular Signal', icon: '📶', passed: false, timestamp: null, description: 'Signal strength reading received',
      detect: (evts) => {
        const e = evts.find((ev: any) => ev.css !== undefined && ev.css !== null && ev.css !== 0);
        if (!e) return { passed: false, time: null };
        const val = Math.abs(parseFloat(e.css));
        const strength = val <= 70 ? 'Strong' : val <= 85 ? 'Moderate' : val <= 100 ? 'Weak' : 'Very Weak';
        const color = val <= 70 ? '#10b981' : val <= 85 ? '#eab308' : val <= 100 ? '#f97316' : '#ef4444';
        return { passed: true, time: e?.time || null, extra: `CSS:${color}:${-val} dBm — ${strength}` };
      }},
    { id: 'ign_on', label: 'Ignition ON', icon: '🔑', passed: false, timestamp: null, description: 'Key turned on (EID 4)',
      detect: (evts) => { const e = evts.find((ev: any) => parseInt(ev.eid) === 4); return { passed: !!e, time: e?.time || null }; }},
    { id: 'ign_off', label: 'Ignition OFF', icon: '🔒', passed: false, timestamp: null, description: 'Key turned off (EID 5)',
      detect: (evts) => { const e = evts.find((ev: any) => parseInt(ev.eid) === 5); return { passed: !!e, time: e?.time || null }; }},
    { id: 'dump_up', label: 'Dump Bin UP', icon: '⬆️', passed: false, timestamp: null, description: 'Dump truck bin raised (Extra 1, EID 14)',
      detect: (evts) => { const e = evts.find((ev: any) => String(ev.extra) === '1' && parseInt(ev.eid) === 14); return { passed: !!e, time: e?.time || null }; }},
    { id: 'dump_down', label: 'Dump Bin DOWN', icon: '⬇️', passed: false, timestamp: null, description: 'Dump truck bin lowered (Extra 1, EID 15)',
      detect: (evts) => { const e = evts.find((ev: any) => String(ev.extra) === '1' && parseInt(ev.eid) === 15); return { passed: !!e, time: e?.time || null }; }},
    { id: 'eng_on', label: 'Engine ON', icon: '⚙️', passed: false, timestamp: null, description: 'Engine running (Extra 4, EID 15)',
      detect: (evts) => { const e = evts.find((ev: any) => String(ev.extra) === '4' && parseInt(ev.eid) === 15); return { passed: !!e, time: e?.time || null }; }},
    { id: 'eng_off', label: 'Engine OFF', icon: '🛑', passed: false, timestamp: null, description: 'Engine stopped (Extra 4, EID 14)',
      detect: (evts) => { const e = evts.find((ev: any) => String(ev.extra) === '4' && parseInt(ev.eid) === 14); return { passed: !!e, time: e?.time || null }; }},
    { id: 'immo_armed', label: 'Keypad Armed', icon: '🔐', passed: false, timestamp: null, description: 'Immobilizer armed (EID 19)',
      detect: (evts) => { const e = evts.find((ev: any) => parseInt(ev.eid) === 19); return { passed: !!e, time: e?.time || null }; }},
    { id: 'immo_disarmed', label: 'Keypad Disarmed', icon: '🔓', passed: false, timestamp: null, description: 'Immobilizer disarmed (EID 20)',
      detect: (evts) => { const e = evts.find((ev: any) => parseInt(ev.eid) === 20); return { passed: !!e, time: e?.time || null }; }},
  ], []);

  const pollDevice = useCallback(async (vehicle: Vehicle, items: CheckItem[], baseline?: string | null) => {
    try {
      const r = await axios.get(`${API_BASE}/api/install-checks/live/${vehicle.platform.toLowerCase()}/${vehicle.unitId}`, { headers: getHeaders() });
      let events = r.data.events || [];
      // If live baseline set, only use events after that timestamp
      if (baseline) {
        const baseTime = new Date(baseline).getTime() - 30000; // 30s buffer before baseline
        events = events.filter((ev: any) => {
          if (!ev.time) return false;
          // Event times from Camel are UTC like "2026-02-16 23:19:54", need to parse as UTC
          const timeStr = String(ev.time).replace(' ', 'T') + (String(ev.time).includes('Z') || String(ev.time).includes('+') ? '' : 'Z');
          const evTime = new Date(timeStr).getTime();
          return !isNaN(evTime) && evTime > baseTime;
        });
      }
      const updated = items.map(item => {
        if (item.passed) return item;
        const result = item.detect(events);
        return result.passed ? { ...item, passed: true, timestamp: result.time ? new Date(String(result.time).replace(' ', 'T') + (String(result.time).includes('Z') ? '' : 'Z')).toISOString() : new Date().toISOString(), extra: result.extra } : item;
      });
      // Grab latest lat/lng
      const locEv = events.find((ev: any) => ev.lat && ev.lng && parseFloat(ev.lat) !== 0 && parseFloat(ev.lng) !== 0);
      if (locEv) setDeviceLocation({ lat: parseFloat(locEv.lat), lng: parseFloat(locEv.lng) });
      return updated;
    } catch (e) { console.error('Poll error:', e); return items; }
  }, []);

  const startCheck = useCallback(async (baseline?: string | null) => {
    if (!selectedVehicle) return;
    const items = buildCheckItems().filter(i => !disabledChecks.has(i.id));
    setCheckItems(items);
    setCheckRunning(true);
    setCheckStartTime(new Date().toISOString());
    setCountdown(refreshRate);

    // Initial poll
    const updated = await pollDevice(selectedVehicle, items, baseline);
    setCheckItems(updated);
    setCountdown(refreshRate);

    // Set up interval
    let currentItems = updated;
    intervalRef.current = setInterval(async () => {
      currentItems = await pollDevice(selectedVehicle, currentItems, baseline);
      setCheckItems([...currentItems]);
      setCountdown(refreshRate);
    }, refreshRate * 1000);

    // Countdown timer
    countdownRef.current = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : refreshRate);
    }, 1000);
  }, [selectedVehicle, refreshRate, buildCheckItems, pollDevice, disabledChecks]);

  const [manualRefreshing, setManualRefreshing] = useState(false);

  const manualPoll = async () => {
    if (!selectedVehicle || !checkRunning) return;
    setManualRefreshing(true);
    const updated = await pollDevice(selectedVehicle, checkItems, liveBaseline);
    setCheckItems(updated);
    setCountdown(refreshRate);
    setManualRefreshing(false);
  };

  const startLiveCheck = async () => {
    if (!selectedVehicle) return;
    stopCheck();
    const baseline = new Date().toISOString();
    setLiveBaseline(baseline);
    setShowLivePopup(true);
    setTimeout(() => setShowLivePopup(false), 3000);
    await startCheck(baseline);
  };

  const stopCheck = () => {
    setCheckRunning(false);
    setLiveBaseline(null);
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
  };

  useEffect(() => { return () => { stopCheck(); }; }, []);

  const loadAllSavedChecks = async () => {
    setLoadingSavedChecks(true);
    try {
      const clientId = currentUser?.client_id || currentUser?.id || 'admin';
      const r = await axios.get(`${API_BASE}/api/install-checks/client/${clientId}`, { headers: getHeaders() });
      setAllSavedChecks(r.data || []);
    } catch (e) { console.error('Load saved checks error:', e); }
    setLoadingSavedChecks(false);
  };

  useEffect(() => {
    if (viewMode === 'saved') loadAllSavedChecks();
  }, [viewMode]);

  const saveCheck = async () => {
    if (!selectedVehicle) return;
    const checkData = {
      client_id: currentUser?.client_id || 'admin',
      unit_id: selectedVehicle.unitId,
      vehicle_name: selectedVehicle.name,
      license_plate: selectedVehicle.licensePlate,
      platform: selectedVehicle.platform,
      fleet: selectedVehicle.fleet || '',
      performed_by: currentUser?.name || currentUser?.username || 'Admin',
      started_at: checkStartTime,
      completed_at: new Date().toISOString(),
      results: checkItems.map(i => ({ id: i.id, label: i.label, passed: i.passed, timestamp: i.timestamp }))
    };
    try {
      await axios.post(`${API_BASE}/api/install-checks`, checkData, { headers: getHeaders() });
      setShowSaveSuccess(true);
      setTimeout(() => setShowSaveSuccess(false), 3000);
    } catch (e) { console.error('Save error:', e); alert('Failed to save check'); }
  };

  const generatePDF = () => {
    if (!selectedVehicle) return;
    setSavingPdf(true);
    try {
      const doc = new jsPDF();
      const now = new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' });
      const W = 210;
      const passed = checkItems.filter(i => i.passed).length;
      const total = checkItems.length;
      const allPassed = passed === total;

      // --- HEADER ---
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, W, 44, 'F');
      // Purple gradient overlay
      doc.setFillColor(139, 92, 246);
      doc.rect(W * 0.6, 0, W * 0.4, 44, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('Installation Check Report', 14, 22);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated: ${now}`, 14, 34);
      doc.text('Optitrac Fleet Management', W - 14, 34, { align: 'right' });

      // --- RESULT BANNER ---
      const bannerY = 52;
      if (allPassed) {
        doc.setFillColor(220, 252, 231); doc.setDrawColor(34, 197, 94);
      } else {
        doc.setFillColor(254, 243, 199); doc.setDrawColor(245, 158, 11);
      }
      doc.roundedRect(14, bannerY, W - 28, 20, 4, 4, 'FD');
      doc.setTextColor(allPassed ? 22 : 146, allPassed ? 101 : 64, allPassed ? 52 : 14);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(allPassed ? 'ALL CHECKS PASSED' : `${passed} of ${total} CHECKS PASSED`, W / 2, bannerY + 13, { align: 'center' });

      // --- VEHICLE INFO BOX ---
      const infoY = 80;
      doc.setFillColor(240, 249, 255);
      doc.setDrawColor(186, 230, 253);
      doc.roundedRect(14, infoY, W - 28, 52, 4, 4, 'FD');
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Vehicle Details', 20, infoY + 10);
      doc.setFontSize(10);
      doc.setTextColor(55, 65, 81);

      const infoItems = [
        ['Vehicle', selectedVehicle.name], ['License Plate', selectedVehicle.licensePlate],
        ['Unit ID', selectedVehicle.unitId], ['Platform', selectedVehicle.platform],
        ['Fleet', selectedVehicle.fleet || '—'], ['Checked By', currentUser?.name || currentUser?.username || 'Admin'],
      ];
      const col1X = 20; const col2X = W / 2 + 5;
      infoItems.forEach(([k, v], i) => {
        const x = i % 2 === 0 ? col1X : col2X;
        const y = infoY + 20 + Math.floor(i / 2) * 10;
        doc.setFont('helvetica', 'bold');
        doc.text(`${k}:`, x, y);
        doc.setFont('helvetica', 'normal');
        doc.text(v, x + 30, y);
      });

      // --- CHECK RESULTS GRID ---
      let curY = 142;
      doc.setTextColor(31, 41, 55);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Check Results', 14, curY);
      curY += 8;

      const cardW = (W - 28 - 8) / 2; // 2 columns with gap
      const cardH = 28;
      const gap = 6;

      // Lay out in pairs (two per row)
      for (let i = 0; i < checkItems.length; i += 2) {
        // Check if this row fits on current page
        if (curY + cardH > 275) {
          doc.addPage();
          curY = 20;
        }

        // Draw left card
        const drawCard = (item: typeof checkItems[0], x: number) => {
          if (item.passed) {
            doc.setFillColor(240, 253, 244); doc.setDrawColor(34, 197, 94);
          } else {
            doc.setFillColor(254, 242, 242); doc.setDrawColor(239, 68, 68);
          }
          doc.roundedRect(x, curY, cardW, cardH, 3, 3, 'FD');

          // Status indicator
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          if (item.passed) {
            doc.setTextColor(34, 197, 94);
            doc.text('PASS', x + cardW - 4, curY + 10, { align: 'right' });
          } else {
            doc.setTextColor(239, 68, 68);
            doc.text('FAIL', x + cardW - 4, curY + 10, { align: 'right' });
          }

          // Label
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(item.passed ? 6 : 153, item.passed ? 95 : 27, item.passed ? 70 : 27);
          doc.text(item.label, x + 5, curY + 10);

          // Timestamp or fail message
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          if (item.passed && item.timestamp) {
            doc.setTextColor(107, 114, 128);
            doc.text(new Date(item.timestamp).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' }), x + 5, curY + 17);
          } else {
            doc.setTextColor(220, 38, 38);
            doc.text('No Record Received', x + 5, curY + 17);
          }

          // Extra info — strip emojis
          if (item.passed && item.extra) {
            doc.setFontSize(8);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(55, 65, 81);
            const cleanExtra = item.extra.replace(/[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\u{FE00}-\u{FEFF}]/gu, '').trim();
            doc.text(cleanExtra, x + 5, curY + 23);
          }
          doc.setTextColor(31, 41, 55);
        };

        drawCard(checkItems[i], 14);
        if (i + 1 < checkItems.length) {
          drawCard(checkItems[i + 1], 14 + cardW + gap);
        }
        curY += cardH + gap;
      }

      // --- LOCATION MAP ---
      if (deviceLocation) {
        // Check if map fits on current page
        if (curY + 70 > 275) {
          doc.addPage();
          curY = 20;
        }

        curY += 6;
        doc.setTextColor(31, 41, 55);
        doc.setFontSize(13);
        doc.setFont('helvetica', 'bold');
        doc.text('Location of Check', 14, curY);
        curY += 8;

        // Add location info box
        doc.setFillColor(240, 249, 255);
        doc.setDrawColor(186, 230, 253);
        doc.roundedRect(14, curY, W - 28, 50, 4, 4, 'FD');
        
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 64, 175);
        doc.text('GPS Coordinates', 20, curY + 12);
        
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(55, 65, 81);
        doc.setFontSize(9);
        doc.text(`Latitude: ${deviceLocation.lat.toFixed(6)}`, 20, curY + 22);
        doc.text(`Longitude: ${deviceLocation.lng.toFixed(6)}`, 20, curY + 30);
        
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text(`View on map: https://www.google.com/maps?q=${deviceLocation.lat},${deviceLocation.lng}`, 20, curY + 40);
      }

      // --- FOOTER ---
      const pageCount = doc.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(8);
        doc.setTextColor(156, 163, 175);
        doc.setFont('helvetica', 'normal');
        doc.text(`Optitrac — Installation Check Report — Page ${p} of ${pageCount}`, W / 2, 290, { align: 'center' });
      }

      doc.save(`Install-Check_${selectedVehicle.name}_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (e) { console.error('PDF error:', e); }
    finally { setSavingPdf(false); }
  };

  const loadHistory = async () => {
    try {
      const cid = currentUser?.client_id || 'admin';
      const r = await axios.get(`${API_BASE}/api/install-checks/client/${cid}`, { headers: getHeaders() });
      setHistory(r.data || []);
      setShowHistory(true);
    } catch (e) { console.error('History error:', e); }
  };

  const platformVehicles = selectedPlatform === 'All' ? vehicles : vehicles.filter(v => v.platform === selectedPlatform);
  const allFleets = Array.from(new Set(platformVehicles.map(v => v.fleet || 'No Fleet').filter(Boolean))).sort();
  const filtered = platformVehicles.filter(v => {
    if (selectedFleets.length > 0 && !selectedFleets.includes(v.fleet || 'No Fleet')) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (v.name || '').toLowerCase().includes(s) || (v.unitId || '').toLowerCase().includes(s) || (v.licensePlate || '').toLowerCase().includes(s);
  });

  const passedCount = checkItems.filter(i => i.passed).length;

  // ---- RENDER ----
  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', borderRadius: '16px', padding: '24px 28px', marginBottom: '24px', color: 'white' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700 }}>🔧 Installation & Configuration Check</h1>
        <p style={{ margin: '6px 0 0', opacity: 0.9, fontSize: '14px' }}>Select a vehicle to run a live installation validation check</p>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
        <button onClick={loadHistory} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: 'white', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
          📋 Check History
        </button>
        <button onClick={() => setShowWiring(true)} style={{ padding: '10px 20px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #ea580c)', color: 'white', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
          🔌 Wiring Information
        </button>
      </div>

      {/* Wiring info modal */}
      {showWiring && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '92%', maxWidth: '600px', maxHeight: '85vh', overflow: 'auto', padding: '0' }}>
            <div style={{ background: 'linear-gradient(135deg, #f59e0b, #ea580c)', padding: '20px 24px', borderRadius: '16px 16px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', color: 'white', fontWeight: 700 }}>🔌 Wiring Guide</h2>
                <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>Cable connections for device installation</p>
              </div>
              <button onClick={() => setShowWiring(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', fontSize: '18px', cursor: 'pointer', color: 'white', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px', display: 'grid', gap: '12px' }}>
              {[
                { color: '#ef4444', wire: 'RED', bg: '#fef2f2', border: '#fecaca', title: 'Power — 12/24V', desc: 'Connect to vehicle battery positive terminal. This is the main power supply to the device. Must be a permanent live feed directly from the battery.' },
                { color: '#1f2937', wire: 'BLACK', bg: '#f3f4f6', border: '#d1d5db', title: 'Ground — Vehicle Battery', desc: 'Connect to vehicle battery negative terminal. Must go directly to the battery ground — do NOT use a local chassis earth point as this can cause voltage drop and unreliable readings.' },
                { color: '#22c55e', wire: 'GREEN', bg: '#f0fdf4', border: '#bbf7d0', title: 'Ignition — Accessory Feed', desc: 'Connect to a wire that is live only when the ignition key is in the accessory/ON position. Must NOT be connected to the engine run circuit. This wire tells the device the key is on.' },
                { color: '#3b82f6', wire: 'BLUE', bg: '#eff6ff', border: '#bfdbfe', title: 'Engine Run — Alternator Signal', desc: 'Connect to the alternator charge light or engine run signal. Must only be active when the engine is physically running. Do NOT connect to the same feed as the green ignition wire — they must be separate circuits.' },
                { color: '#f97316', wire: 'ORANGE', bg: '#fff7ed', border: '#fed7aa', title: 'Auxiliary Input — Signal Wire', desc: 'Used for dump truck tray up/down, pump activation, or any auxiliary switch signal. This is a negative trigger (pull to ground). Connect to the signal source that provides a ground when the action is active (e.g. tray raised, pump on).' },
                { color: '#eab308', wire: 'YELLOW', bg: '#fefce8', border: '#fde68a', title: 'Output — Ignition Kill Relay', desc: 'This is a negative output only — it provides a ground signal to activate a relay. Connect to the relay coil for ignition kill. When triggered, it pulls to ground to cut the ignition circuit. Must be wired through a relay — do NOT connect directly to the ignition wire.' },
              ].map((w, i) => (
                <div key={i} style={{ padding: '16px', backgroundColor: w.bg, border: `2px solid ${w.border}`, borderRadius: '12px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: w.color, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 12px ${w.color}40` }}>
                    <span style={{ color: w.wire === 'BLACK' ? '#fff' : '#fff', fontSize: '11px', fontWeight: 800 }}>{w.wire}</span>
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: w.color, marginBottom: '4px' }}>{w.title}</div>
                    <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5' }}>{w.desc}</div>
                  </div>
                </div>
              ))}
              <div style={{ padding: '14px', backgroundColor: '#fefce8', border: '1px solid #fde68a', borderRadius: '10px', marginTop: '4px' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#854d0e', marginBottom: '4px' }}>⚠️ Important Notes</div>
                <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '12px', color: '#713f12', lineHeight: '1.8' }}>
                  <li>Always disconnect the vehicle battery before starting installation</li>
                  <li>Use proper crimped connectors — no twisted wires or electrical tape</li>
                  <li>Green (ignition) and Blue (engine) must be on separate circuits</li>
                  <li>Black ground must go to battery negative, not chassis</li>
                  <li>Orange input is a negative trigger — it pulls to ground when active</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', width: '90%', maxWidth: '700px', maxHeight: '80vh', overflow: 'auto', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>📋 Check History</h2>
              <button onClick={() => setShowHistory(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
            </div>
            {history.length === 0 ? <p style={{ color: '#6b7280' }}>No checks recorded yet.</p> : (
              history.map((h: any, i: number) => (
                <div key={i} style={{ padding: '14px', border: '1px solid #e5e7eb', borderRadius: '10px', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: 600 }}>
                    <span>{h.vehicle_name} ({h.unit_id})</span>
                    <span style={{ color: '#6b7280', fontWeight: 400 }}>{h.completed_at ? new Date(h.completed_at).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' }) : '—'}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    By: {h.performed_by} | Platform: {h.platform} | Passed: {h.results?.filter((r: any) => r.passed).length}/{h.results?.length}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Vehicle picker or active check */}
      {!selectedVehicle ? (
        <>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            {['All', 'Camel', 'Starlink'].map(p => (
              <button key={p} onClick={() => { setSelectedPlatform(p); setSelectedFleets([]); }}
                style={{ padding: '8px 20px', borderRadius: '10px', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer',
                  background: selectedPlatform === p ? (p === 'Camel' ? '#1d4ed8' : p === 'Starlink' ? '#92400e' : 'linear-gradient(135deg, #6366f1, #8b5cf6)') : '#f3f4f6',
                  color: selectedPlatform === p ? 'white' : '#6b7280' }}>
                {p} ({p === 'All' ? vehicles.length : vehicles.filter(v => v.platform === p).length})
              </button>
            ))}
          </div>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search vehicles..."
            style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #d1d5db', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' }} />
          {allFleets.length > 1 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
              {allFleets.map(f => {
                const count = platformVehicles.filter(v => (v.fleet || 'No Fleet') === f).length;
                return (
                <label key={f} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '8px', border: `1px solid ${selectedFleets.includes(f) ? '#3b82f6' : '#e5e7eb'}`, backgroundColor: selectedFleets.includes(f) ? '#eff6ff' : 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: selectedFleets.includes(f) ? '#1d4ed8' : '#6b7280' }}>
                  <input type="checkbox" checked={selectedFleets.includes(f)} onChange={() => {
                    setSelectedFleets(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]);
                  }} style={{ accentColor: '#3b82f6' }} />
                  {f} <span style={{ fontSize: '11px', opacity: 0.7 }}>({count})</span>
                </label>
              );})}
            </div>
          )}
          {loading ? <p>Loading fleet...</p> : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
              {filtered.map(v => (
                <div key={v.unitId} onClick={() => setSelectedVehicle(v)}
                  style={{ padding: '16px', border: '1px solid #e5e7eb', borderRadius: '12px', backgroundColor: 'white', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                  onMouseOver={e => (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)')}
                  onMouseOut={e => (e.currentTarget.style.boxShadow = 'none')}>
                  <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>{v.name}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    <span style={{ padding: '2px 8px', borderRadius: '6px', backgroundColor: v.platform === 'Camel' ? '#dbeafe' : '#fef3c7', color: v.platform === 'Camel' ? '#1d4ed8' : '#92400e', fontWeight: 600, marginRight: '6px' }}>{v.platform}</span>
                    {v.unitId}
                  </div>
                  {v.licensePlate && v.licensePlate !== v.name && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>🚗 {v.licensePlate}</div>}
                  {v.fleet && <div style={{ fontSize: '11px', color: '#8b5cf6', marginTop: '4px', fontWeight: 600 }}>📁 {v.fleet}</div>}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div>
          {/* Vehicle info bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '12px', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <span style={{ fontWeight: 700, fontSize: '16px' }}>{selectedVehicle.name}</span>
              <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>Unit: {selectedVehicle.unitId} | Plate: {selectedVehicle.licensePlate} | {selectedVehicle.platform}</span>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {!checkRunning ? (
                <>
                  <button onClick={() => startCheck()} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>
                    ▶️ Start Check
                  </button>
                  <button onClick={startLiveCheck} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #ea580c)', color: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>
                    📡 Live Check
                  </button>
                </>
              ) : (
                <>
                  <button onClick={stopCheck} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: '#ef4444', color: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>
                    ⏹ Stop
                  </button>
                  {!liveBaseline && (
                    <button onClick={startLiveCheck} style={{ padding: '10px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #ea580c)', color: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>
                      📡 Live Check
                    </button>
                  )}
                </>
              )}
              <button onClick={() => { stopCheck(); setSelectedVehicle(null); setCheckItems([]); setDeviceLocation(null); }} style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #d1d5db', backgroundColor: 'white', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                ← Back
              </button>
              {!isClient && (
                <button onClick={() => {
                  const platform = selectedVehicle.platform.toLowerCase();
                  localStorage.setItem('platformSearch', selectedVehicle.unitId);
                  localStorage.setItem('platformView', platform);
                  window.location.href = '/';
                }} style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #3b82f6', backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
                  🔗 View in {selectedVehicle.platform}
                </button>
              )}
            </div>
          </div>

          {/* Optional checks toggle - show before check starts */}
          {!checkRunning && (
            <div style={{ padding: '14px 18px', backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '12px', marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '10px' }}>Optional Checks — untick items not needed for this vehicle:</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {buildCheckItems().filter(i => optionalChecks.has(i.id)).map(item => (
                  <label key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', border: `1px solid ${disabledChecks.has(item.id) ? '#e5e7eb' : '#3b82f6'}`, backgroundColor: disabledChecks.has(item.id) ? '#f3f4f6' : '#eff6ff', cursor: 'pointer', fontSize: '13px', color: disabledChecks.has(item.id) ? '#9ca3af' : '#1d4ed8', fontWeight: 600, textDecoration: disabledChecks.has(item.id) ? 'line-through' : 'none' }}>
                    <input type="checkbox" checked={!disabledChecks.has(item.id)} onChange={() => {
                      setDisabledChecks(prev => { const n = new Set(prev); n.has(item.id) ? n.delete(item.id) : n.add(item.id); return n; });
                    }} style={{ accentColor: '#3b82f6' }} />
                    {item.icon} {item.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Live check popup */}
          {showLivePopup && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '32px 40px', maxWidth: '420px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📡</div>
                <h3 style={{ margin: '0 0 8px', fontSize: '20px', color: '#1f2937' }}>Live Check Started</h3>
                <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '14px', lineHeight: '1.6' }}>
                  Reading latest records now. Only <strong>new data</strong> from this point forward will be used — no previous records will count.
                </p>
                <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>This popup will close automatically...</p>
              </div>
            </div>
          )}

          {/* Live mode indicator */}
          {checkRunning && liveBaseline && (
            <div style={{ padding: '10px 16px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <span style={{ fontSize: '18px' }}>📡</span>
              <span style={{ fontWeight: 700, color: '#ea580c' }}>LIVE MODE</span>
              <span style={{ color: '#6b7280' }}>— Only reading new data since {new Date(liveBaseline).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
            </div>
          )}

          {/* Refresh rate + countdown */}
          {checkRunning && (
            <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px', fontSize: '13px', color: '#6b7280' }}>
              <span>Refresh every:
                <select value={refreshRate} onChange={e => setRefreshRate(Number(e.target.value))} style={{ marginLeft: '6px', padding: '4px 8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value={5}>5s</option><option value={10}>10s</option><option value={20}>20s</option><option value={30}>30s</option><option value={60}>60s</option>
                </select>
              </span>
              <span>Next refresh in: <strong style={{ color: '#3b82f6' }}>{countdown}s</strong></span>
              <button onClick={manualPoll} disabled={manualRefreshing} style={{ padding: '6px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: 'white', fontWeight: 600, fontSize: '12px', cursor: manualRefreshing ? 'default' : 'pointer', opacity: manualRefreshing ? 0.6 : 1 }}>
                {manualRefreshing ? '⏳ Checking...' : '🔄 Refresh Now'}
              </button>
              <span>Passed: <strong style={{ color: '#10b981' }}>{passedCount}/{checkItems.length}</strong></span>
            </div>
          )}

          {/* Check items grid */}
          {checkItems.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px', marginBottom: '20px' }}>
              {checkItems.map(item => {
                const isLowVoltage = item.passed && item.extra?.includes('LOW VOLTAGE');
                const isCss = item.id === 'css' && item.passed && item.extra?.startsWith('CSS:');
                const cssColor = isCss ? item.extra!.split(':')[1] : null;
                const cssText = isCss ? item.extra!.split(':').slice(2).join(':') : null;
                return (
                <div key={item.id} style={{
                  padding: '16px', borderRadius: '12px',
                  border: `2px solid ${isLowVoltage ? '#f59e0b' : item.passed ? '#10b981' : '#e5e7eb'}`,
                  backgroundColor: isCss && cssColor ? `${cssColor}18` : item.passed ? '#f0fdf4' : 'white',
                  transition: 'all 0.3s',
                  animation: isLowVoltage ? 'pulse-amber 1.5s infinite' : undefined,
                  position: 'relative'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '28px' }}>{item.icon}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: isLowVoltage ? '#b45309' : item.passed ? '#065f46' : '#374151' }}>{item.label}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{item.description}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: '28px' }}>{item.passed ? (isLowVoltage ? '⚠️' : '✅') : '⏳'}</span>
                  </div>
                  {item.passed && item.timestamp && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: isLowVoltage ? '#b45309' : '#059669', fontWeight: 600 }}>
                      ✓ {new Date(item.timestamp).toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' })}
                    </div>
                  )}
                  {item.passed && item.extra && !isCss && (
                    <div style={{ marginTop: '4px', fontSize: '12px', fontWeight: 700, color: isLowVoltage ? '#dc2626' : '#374151' }}>
                      {item.extra}
                    </div>
                  )}
                  {isCss && cssText && (
                    <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: cssColor || '#999' }} />
                      <span style={{ fontSize: '13px', fontWeight: 700, color: cssColor || '#374151' }}>{cssText}</span>
                      <button onClick={() => setShowCssInfo(true)} style={{ marginLeft: 'auto', padding: '3px 10px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', fontSize: '11px', fontWeight: 700, cursor: 'pointer', color: '#6b7280' }}>
                        ℹ️ Signal Guide
                      </button>
                    </div>
                  )}
                </div>
              );})}
            </div>
          )}

          {/* CSS Signal Guide popup */}
          {showCssInfo && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setShowCssInfo(false)}>
              <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', maxWidth: '400px', width: '90%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>📶 CSS Signal Strength Guide</h3>
                  <button onClick={() => setShowCssInfo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                </div>
                {[
                  { range: '0 to -70 dBm', label: 'Strong', desc: 'Excellent — reliable connection', color: '#10b981', bg: '#f0fdf4' },
                  { range: '-71 to -85 dBm', label: 'Moderate', desc: 'Acceptable — normal operation', color: '#eab308', bg: '#fefce8' },
                  { range: '-86 to -100 dBm', label: 'Weak', desc: 'Unreliable — may drop data', color: '#f97316', bg: '#fff7ed' },
                  { range: 'Below -100 dBm', label: 'Very Weak', desc: 'Poor — likely data loss', color: '#ef4444', bg: '#fef2f2' },
                ].map(r => (
                  <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderRadius: '10px', backgroundColor: r.bg, border: `1px solid ${r.color}30`, marginBottom: '8px' }}>
                    <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: r.color, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: r.color }}>{r.label} — {r.range}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>{r.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Map view */}
          {deviceLocation && (
            <div style={{ marginTop: '16px', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', backgroundColor: '#f0f9ff', borderBottom: '1px solid #e5e7eb', fontSize: '13px', fontWeight: 700, color: '#1e40af' }}>
                📍 Device Location — {deviceLocation.lat.toFixed(6)}, {deviceLocation.lng.toFixed(6)}
              </div>
              <iframe
                title="Device Location"
                width="100%" height="300" style={{ border: 'none' }}
                src={`https://www.google.com/maps?q=${deviceLocation.lat},${deviceLocation.lng}&t=k&z=17&output=embed`}
              />
            </div>
          )}

          {/* Action buttons after check */}
          {checkItems.length > 0 && !checkRunning && (
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button onClick={saveCheck} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>
                💾 Save Check
              </button>
              <button onClick={generatePDF} disabled={savingPdf} style={{ padding: '12px 24px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer', opacity: savingPdf ? 0.6 : 1 }}>
                {savingPdf ? '⏳ Generating...' : '📄 Download PDF'}
              </button>
            </div>
          )}
        </div>
      )}
      <HelpGuide />

      {/* Save success popup */}
      {showSaveSuccess && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'white', borderRadius: '16px', padding: '32px 40px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', zIndex: 10000, textAlign: 'center', animation: 'slideUp 0.3s ease-out' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px', animation: 'checkPop 0.5s ease-out' }}>✓</div>
          <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '700', color: '#10b981' }}>Check Saved!</h3>
          <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>Installation check has been saved successfully</p>
        </div>
      )}
    </div>
  );
};

export default InstallCheck;
